import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../store/gameStore';
import { usePremium } from '../hooks/usePremium';
import { fetchGames, getTeam, getSprayChartData } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Target, Lock, Crown } from 'lucide-react';
import { cn } from '../lib/utils';

const HIT_COLORS = {
  single: '#22c55e',
  double: '#3b82f6',
  triple: '#8b5cf6',
  hr: '#ef4444',
  groundout: '#9ca3af',
  flyout: '#9ca3af',
  error: '#f59e0b'
};

const SprayChartField = ({ data, selectedResult }) => {
  const filteredData = selectedResult === 'all' 
    ? data 
    : data.filter(d => d.result === selectedResult);

  return (
    <div className="relative w-full aspect-square max-w-md mx-auto">
      {/* Field background */}
      <svg 
        viewBox="0 0 100 100" 
        className="w-full h-full"
        style={{ background: '#166534' }}
      >
        {/* Outfield grass */}
        <path
          d="M 50 95 L 5 50 Q 50 -5 95 50 Z"
          fill="#14532d"
        />
        
        {/* Warning track */}
        <path
          d="M 50 95 L 10 55 Q 50 5 90 55 Z"
          fill="none"
          stroke="#c2885c"
          strokeWidth="3"
          opacity="0.5"
        />
        
        {/* Infield dirt */}
        <path
          d="M 50 90 L 70 70 L 50 55 L 30 70 Z"
          fill="#c2885c"
          opacity="0.8"
        />
        
        {/* Foul lines */}
        <line x1="50" y1="95" x2="5" y2="50" stroke="white" strokeWidth="0.5" />
        <line x1="50" y1="95" x2="95" y2="50" stroke="white" strokeWidth="0.5" />
        
        {/* Base paths */}
        <line x1="50" y1="90" x2="70" y2="70" stroke="white" strokeWidth="0.3" />
        <line x1="70" y1="70" x2="50" y2="55" stroke="white" strokeWidth="0.3" />
        <line x1="50" y1="55" x2="30" y2="70" stroke="white" strokeWidth="0.3" />
        <line x1="30" y1="70" x2="50" y2="90" stroke="white" strokeWidth="0.3" />
        
        {/* Bases */}
        <rect x="48" y="88" width="4" height="4" fill="white" transform="rotate(45 50 90)" />
        <rect x="68" y="68" width="3" height="3" fill="white" transform="rotate(45 70 70)" />
        <rect x="48" y="53" width="3" height="3" fill="white" transform="rotate(45 50 55)" />
        <rect x="28" y="68" width="3" height="3" fill="white" transform="rotate(45 30 70)" />
        
        {/* Hit markers */}
        {filteredData.map((hit, idx) => (
          <circle
            key={idx}
            cx={hit.x}
            cy={hit.y}
            r="2.5"
            fill={HIT_COLORS[hit.result] || '#9ca3af'}
            stroke="white"
            strokeWidth="0.5"
            className="transition-all hover:r-4"
          >
            <title>{hit.result.toUpperCase()}</title>
          </circle>
        ))}
      </svg>
    </div>
  );
};

export default function SprayChartsPage() {
  const navigate = useNavigate();
  const { currentTeamId } = useGameStore();
  const { isPremium } = usePremium();
  const [team, setTeam] = useState(null);
  const [games, setGames] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedGame, setSelectedGame] = useState('all');
  const [selectedResult, setSelectedResult] = useState('all');
  const [sprayData, setSprayData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  const canAccessSprayCharts = isPremium;

  useEffect(() => {
    if (!currentTeamId) {
      navigate('/');
      return;
    }
    if (!canAccessSprayCharts) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    loadInitialData();
  }, [currentTeamId, canAccessSprayCharts]);

  useEffect(() => {
    if (selectedPlayer) {
      loadSprayData();
    }
  }, [selectedPlayer, selectedGame]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [teamData, gamesData] = await Promise.all([
        getTeam(currentTeamId),
        fetchGames(currentTeamId)
      ]);
      setTeam(teamData);
      setGames(gamesData);
      
      if (teamData.players?.length > 0) {
        setSelectedPlayer(teamData.players[0].id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSprayData = async () => {
    if (!selectedPlayer) return;
    
    try {
      const data = await getSprayChartData(
        selectedPlayer,
        selectedGame !== 'all' ? selectedGame : undefined
      );
      setSprayData(data);
    } catch (error) {
      console.error('Error loading spray data:', error);
      setSprayData([]);
    }
  };

  const selectedPlayerData = team?.players?.find(p => p.id === selectedPlayer);

  // Calculate stats for selected player
  const hitStats = sprayData.reduce((acc, hit) => {
    acc[hit.result] = (acc[hit.result] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="w-12 h-12 border-4 border-green-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show upgrade prompt if access denied
  if (accessDenied) {
    return (
      <div className="min-h-screen bg-stone-100 p-4 md:p-8" data-testid="spray-charts-page">
        <div className="max-w-lg mx-auto mt-20">
          <Card className="border-0 shadow-xl text-center">
            <CardContent className="pt-12 pb-8">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="w-10 h-10 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-stone-800 mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                PREMIUM FEATURE
              </h2>
              <p className="text-stone-500 mb-6">
                Spray Charts are part of Diamond Scorer Premium. Upgrade to visualize where your players hit the ball — comes with AI Lineup Scan, PDF Player Report Cards, Season Milestones, and unlimited season history.
              </p>
              <Button
                onClick={() => navigate('/upgrade?feature=Spray%20Charts')}
                className="bg-amber-500 hover:bg-amber-600"
                data-testid="upgrade-spray-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to Premium
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 p-4 md:p-8" data-testid="spray-charts-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-extrabold text-green-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Target className="inline mr-2" size={28} />
              SPRAY CHARTS
            </h1>
            <p className="text-stone-600">{team?.name}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Controls */}
          <div className="space-y-4">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  SELECT PLAYER
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={selectedPlayer || ''} onValueChange={setSelectedPlayer}>
                  <SelectTrigger data-testid="player-select">
                    <SelectValue placeholder="Select player" />
                  </SelectTrigger>
                  <SelectContent>
                    {team?.players?.map((player) => (
                      <SelectItem key={player.id} value={player.id}>
                        {player.jersey_number ? `#${player.jersey_number} ` : ''}{player.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  FILTER
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm text-stone-500 mb-1 block">Game</label>
                  <Select value={selectedGame} onValueChange={setSelectedGame}>
                    <SelectTrigger data-testid="game-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Games</SelectItem>
                      {games.map((game) => (
                        <SelectItem key={game.id} value={game.id}>
                          {game.date} vs {game.opponent_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm text-stone-500 mb-1 block">Hit Type</label>
                  <Select value={selectedResult} onValueChange={setSelectedResult}>
                    <SelectTrigger data-testid="result-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Results</SelectItem>
                      <SelectItem value="single">Singles</SelectItem>
                      <SelectItem value="double">Doubles</SelectItem>
                      <SelectItem value="triple">Triples</SelectItem>
                      <SelectItem value="hr">Home Runs</SelectItem>
                      <SelectItem value="error">Errors</SelectItem>
                      <SelectItem value="groundout">Ground Outs</SelectItem>
                      <SelectItem value="flyout">Fly Outs</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Legend */}
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  LEGEND
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(HIT_COLORS).map(([type, color]) => (
                    <div key={type} className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded-full border border-white"
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-sm capitalize">{type}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Spray Chart */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {selectedPlayerData 
                    ? `${selectedPlayerData.jersey_number ? '#' + selectedPlayerData.jersey_number + ' ' : ''}${selectedPlayerData.name}`
                    : 'SELECT A PLAYER'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sprayData.length === 0 ? (
                  <div className="text-center py-12 text-stone-500">
                    <Target size={48} className="mx-auto mb-4 opacity-30" />
                    <p>No spray data available</p>
                    <p className="text-sm mt-1">Record at-bats with spray locations to see data here</p>
                  </div>
                ) : (
                  <SprayChartField data={sprayData} selectedResult={selectedResult} />
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            {sprayData.length > 0 && (
              <div className="grid grid-cols-5 gap-4 mt-4">
                <Card className="border-0 shadow-md">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-green-600" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {hitStats.single || 0}
                    </p>
                    <p className="text-xs text-stone-500 uppercase">Singles</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-md">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {hitStats.double || 0}
                    </p>
                    <p className="text-xs text-stone-500 uppercase">Doubles</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-md">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-purple-600" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {hitStats.triple || 0}
                    </p>
                    <p className="text-xs text-stone-500 uppercase">Triples</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-md">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {hitStats.hr || 0}
                    </p>
                    <p className="text-xs text-stone-500 uppercase">Home Runs</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-md">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {hitStats.error || 0}
                    </p>
                    <p className="text-xs text-stone-500 uppercase">Errors</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
